/**
 * SEO Utility for Thrayam Toys
 * Manages dynamic meta tags, structured data, and page titles.
 * The backend (src/seo/renderer.js) renders the same tags server-side from
 * the shared JSON in src/data, so keep the two in step.
 */
import PAGES from '../data/pages.json';
import FAQ from '../data/faq.json';

const SITE_NAME    = 'Thrayam Toys';
const SITE_URL     = 'https://thrayamtoys.com';
const SITE_PHONE   = '+918807259471';
const SITE_EMAIL   = 'info@thrayamtoys.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg?v=2`;
const TWITTER_HANDLE = '@thrayamtoys';

/* ── Product URL (readable slug, falls back to id) ─────────────── */
export const productPath = (product) => `/products/${product?.slug || product?._id}`;

/* ── Title ──────────────────────────────────────────────────────── */
// Brand suffix only when the title doesn't already name us and stays within
// the ~60 characters Google shows (same rule as backend/src/seo/renderer.js)
const TITLE_MAX = 60;
export const fullPageTitle = (title) => {
  if (!title) return `${SITE_NAME} – Wooden & Educational Toys India`;
  const withBrand = `${title} | ${SITE_NAME}`;
  return title.includes(SITE_NAME) || withBrand.length > TITLE_MAX ? title : withBrand;
};

export const setPageTitle = (title) => {
  document.title = fullPageTitle(title);
};

/* ── Meta description ───────────────────────────────────────────── */
export const setMetaDescription = (description) => {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'description';
    document.head.appendChild(tag);
  }
  tag.content = description;
};

/* ── Meta keywords ──────────────────────────────────────────────── */
export const setMetaKeywords = (keywords) => {
  let tag = document.querySelector('meta[name="keywords"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'keywords';
    document.head.appendChild(tag);
  }
  tag.content = Array.isArray(keywords) ? keywords.join(', ') : keywords;
};

/* ── Robots directive ──────────────────────────────────────────── */
export const setRobots = (content) => {
  let tag = document.querySelector('meta[name="robots"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'robots';
    document.head.appendChild(tag);
  }
  tag.content = content;
};

/* ── Helpers ────────────────────────────────────────────────────── */
const absImage = (img) => {
  const url = typeof img === 'string' ? img : img?.url;
  if (!url) return null;
  return url.startsWith('http') ? url : `${SITE_URL}/uploads/${url}`;
};

// Product ageGroup → schema.org audience age range (years)
const AGE_RANGE = {
  '0-6 months': [0, 0.5], '6-12 months': [0.5, 1], '1-2 years': [1, 2], '2-3 years': [2, 3],
  '3-5 years': [3, 5], '5-8 years': [5, 8], '8-12 years': [8, 12], '12+ years': [12, 99],
};

/* ── Canonical URL ──────────────────────────────────────────────── */
export const setCanonicalUrl = (url) => {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = 'canonical';
    document.head.appendChild(tag);
  }
  tag.href = url.startsWith('http') ? url : `${SITE_URL}${url}`;
};

/* ── Open Graph ─────────────────────────────────────────────────── */
export const setOpenGraphTags = (og) => {
  const ogTags = {
    'og:site_name':  SITE_NAME,
    'og:locale':     'en_IN',
    'og:title':      og.title || document.title,
    'og:description':og.description || '',
    'og:image':      og.image || DEFAULT_IMAGE,
    'og:image:secure_url': og.image || DEFAULT_IMAGE,
    'og:image:width': '1200',
    'og:image:height':'630',
    'og:url':        og.url ? (og.url.startsWith('http') ? og.url : `${SITE_URL}${og.url}`) : window.location.href,
    'og:type':       og.type || 'website',
  };
  Object.entries(ogTags).forEach(([property, content]) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      document.head.appendChild(tag);
    }
    tag.content = content;
  });
};

/* ── Twitter Card ───────────────────────────────────────────────── */
export const setTwitterTags = (twitter) => {
  const twitterTags = {
    'twitter:card':        'summary_large_image',
    'twitter:site':        TWITTER_HANDLE,
    'twitter:creator':     TWITTER_HANDLE,
    'twitter:title':       twitter.title || document.title,
    'twitter:description': twitter.description || '',
    'twitter:image':       twitter.image || DEFAULT_IMAGE,
    'twitter:image:alt':   twitter.imageAlt || `${SITE_NAME} - Safe Wooden Toys for Kids`,
  };
  Object.entries(twitterTags).forEach(([name, content]) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.name = name;
      document.head.appendChild(tag);
    }
    tag.content = content;
  });
};

/* ── Structured Data ────────────────────────────────────────────── */
export const setStructuredData = (data, id = 'page-structured-data') => {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

/* ── Product Schema ─────────────────────────────────────────────── */
export const generateProductSchema = (product) => {
  const price = product.discountPrice || product.price;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description?.replace(/<[^>]+>/g, '').slice(0, 300),
    image: (product.images || []).map(absImage).filter(Boolean),
    sku: product._id?.slice(-8).toUpperCase(),
    mpn: product._id,
    brand: { '@type': 'Brand', name: SITE_NAME },
    category: product.category?.name || 'Wooden Toys',
    material: product.material || 'Wood',
    audience: {
      '@type': 'PeopleAudience',
      suggestedMinAge: (AGE_RANGE[product.ageGroup] || [0, 12])[0],
      suggestedMaxAge: (AGE_RANGE[product.ageGroup] || [0, 12])[1],
    },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}${productPath(product)}`,
      priceCurrency: 'INR',
      price: price,
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: product.stock > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: price >= 499 ? 0 : 49,
          currency: 'INR',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'IN',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
          transitTime:  { '@type': 'QuantitativeValue', minValue: 3, maxValue: 7, unitCode: 'DAY' },
        },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'IN',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 7,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      seller: { '@type': 'Organization', name: SITE_NAME },
    },
  };
  // Only emit ratings backed by real reviews — invented ratings violate Google's guidelines
  if (product.numReviews > 0 && product.averageRating > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.averageRating,
      bestRating: 5,
      worstRating: 1,
      reviewCount: product.numReviews,
    };
  }
  return schema;
};

/* ── Breadcrumb Schema ──────────────────────────────────────────── */
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
  })),
});

/* ── Collection / ItemList Schema ───────────────────────────────── */
export const generateCollectionSchema = (category, products = []) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: category.name || 'Wooden Toys Collection',
  description: category.description || `Shop ${category.name || 'wooden toys'} at ${SITE_NAME} — safe, educational & handcrafted.`,
  url: `${SITE_URL}/products${category._id && category._id !== 'all' ? `?category=${category._id}` : ''}`,
  mainEntity: {
    '@type': 'ItemList',
    numberOfItems: products.length,
    itemListElement: products.slice(0, 12).map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: product.name,
        url: `${SITE_URL}${productPath(product)}`,
        image: absImage(product.images?.[0]) || DEFAULT_IMAGE,
        offers: {
          '@type': 'Offer',
          price: product.discountPrice || product.price,
          priceCurrency: 'INR',
          availability: product.stock > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        },
        aggregateRating: product.numReviews > 0 && product.averageRating > 0 ? {
          '@type': 'AggregateRating',
          ratingValue: product.averageRating,
          reviewCount: product.numReviews,
        } : undefined,
      },
    })),
  },
});

/* ── FAQ Schema ─────────────────────────────────────────────────── */
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

export const DEFAULT_FAQ = FAQ;

/* ── Local Business Schema ──────────────────────────────────────── */
export const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['Store', 'OnlineStore'],
  '@id': `${SITE_URL}/#store`,
  name: SITE_NAME,
  alternateName: 'Thrayam Toys – Safe Wooden Toys India',
  description: 'Premium handcrafted wooden toys for babies, toddlers and kids. Safe, non-toxic, educational toys shop online in India with free shipping above ₹499.',
  url: SITE_URL,
  telephone: SITE_PHONE,
  email: SITE_EMAIL,
  priceRange: '₹₹',
  currenciesAccepted: 'INR',
  paymentAccepted: 'Cash, Credit Card, Debit Card, UPI, Net Banking, Wallets',
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '09:00',
    closes: '20:00',
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN',
    addressRegion: 'Tamil Nadu',
  },
  logo: `${SITE_URL}/logo-512.png?v=2`,
  image: DEFAULT_IMAGE,
  sameAs: [
    'https://www.facebook.com/thrayamtoys',
    'https://www.instagram.com/thrayamtoys',
    'https://www.youtube.com/@thrayamtoys',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Wooden & Educational Toys',
    itemListElement: [
      { '@type': 'OfferCatalog', name: 'Wooden Toys for Babies' },
      { '@type': 'OfferCatalog', name: 'Montessori Toys for Toddlers' },
      { '@type': 'OfferCatalog', name: 'Educational STEM Toys' },
      { '@type': 'OfferCatalog', name: 'Gift Sets for Kids' },
    ],
  },
});

/* ── Unified setSEO ─────────────────────────────────────────────── */
export const setSEO = (config) => {
  const { title, description, keywords, url, image, imageAlt, type = 'website', structuredData } = config;

  setPageTitle(title);
  if (description) setMetaDescription(description);
  if (keywords)    setMetaKeywords(keywords);
  if (url)         setCanonicalUrl(url);

  const fullTitle = title ? fullPageTitle(title) : undefined;
  setOpenGraphTags({ title: fullTitle, description, url, image, type });
  setTwitterTags({ title: fullTitle, description, image, imageAlt });

  if (structuredData) setStructuredData(structuredData);
};

/* ── Page-level SEO configs ─────────────────────────────────────── */
const pageSeo = (path) => ({ ...PAGES[path], url: path });

export const PAGE_SEO = {
  home:       pageSeo('/'),
  products:   pageSeo('/products'),
  about:      pageSeo('/about'),
  contact:    pageSeo('/contact'),
  blogs:      pageSeo('/blogs'),
  giftGuide:  pageSeo('/gift-guide'),
  giftCards:  pageSeo('/gift-cards'),
  bulkOrders: pageSeo('/bulk-orders'),
};

// Every fixed-copy route, keyed by path (used by AppContent on navigation)
export const PAGES_BY_PATH = PAGES;

/* ── Product alt text ───────────────────────────────────────────── */
export const generateProductAltText = (product, index = 0) => {
  const parts = [product.name];
  if (product.material)          parts.push(product.material);
  if (product.category?.name)    parts.push(product.category.name);
  if (product.ageGroup)          parts.push(`for ${product.ageGroup}`);
  const suffix = index > 0 ? ` – Image ${index + 1}` : '';
  return `${parts.join(' – ')}${suffix} | ${SITE_NAME}`;
};

const seoUtils = {
  setSEO, setPageTitle, setRobots, setMetaDescription, setMetaKeywords,
  setCanonicalUrl, setOpenGraphTags, setTwitterTags,
  setStructuredData, generateProductSchema, generateBreadcrumbSchema,
  generateCollectionSchema, generateFAQSchema, generateLocalBusinessSchema,
  generateProductAltText, productPath, DEFAULT_FAQ, PAGE_SEO, PAGES_BY_PATH,
};

export default seoUtils;
